import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../constants/api';
import ContactLogo from '../components/shared/ContactLogo';
import { useAuth } from '../contexts/AuthContext';

const PublicResetPasswordPage = () => {
  const { loginWithToken } = useAuth();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    
    if (tokenParam) {
      setToken(tokenParam);
      verifyToken(tokenParam);
    } else {
      setError('Token de restablecimiento no proporcionado');
      setVerifying(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    setVerifying(true);
    try {
      const response = await axios.get(`${API}/auth/verify-reset-token/${tokenToVerify}`);
      if (response.data.valid) {
        setTokenValid(true);
        setEmail(response.data.email);
      } else {
        setError(response.data.message || 'Token inválido');
        setTokenValid(false);
      }
    } catch (error) {
      setError('Error al verificar el enlace');
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'La contraseña debe tener al menos 8 caracteres';
    }
    if (!/[A-Z]/.test(password)) {
      return 'La contraseña debe incluir al menos una letra mayúscula';
    }
    if (!/[a-z]/.test(password)) {
      return 'La contraseña debe incluir al menos una letra minúscula';
    }
    if (!/[0-9]/.test(password)) {
      return 'La contraseña debe incluir al menos un número';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });

      const { access_token, user: userData } = response.data;
      if (access_token && userData) {
        // The backend already issues a fresh session token for this reset —
        // use it to log the user straight in instead of sending them back
        // to the login form with a password they just typed in.
        loginWithToken(access_token, userData);
        navigate('/dashboard');
      } else {
        navigate('/auth/reset/success');
      }

    } catch (error) {
      setError(error.response?.data?.detail || 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-contact flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <ContactLogo className="h-12 w-auto mx-auto" />
            <div className="mt-6 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <p className="mt-4 text-sm text-gray-600">Verificando enlace...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-contact flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <ContactLogo className="h-12 w-auto mx-auto" />
            <div className="mt-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h2 className="mt-4 text-xl font-bold text-gray-900">Enlace inválido</h2>
            <p className="mt-2 text-sm text-gray-600">El enlace no es válido o ha expirado. Solicita uno nuevo.</p>
            <div className="mt-6 space-y-3">
              <button
                onClick={() => navigate('/auth/forgot-password')}
                className="btn-primary"
              >
                Solicitar nuevo enlace
              </button>
              <div>
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Volver al inicio de sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-contact flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <ContactLogo className="h-12 w-auto mx-auto" />
          <h2 className="mt-6 text-2xl font-bold text-black">
            Crear nueva contraseña
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Para: {email}
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 card-shadow-lg sm:rounded-xl sm:px-10 border border-gray-100">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="error-state">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="new-password" className="form-label">
                Nueva contraseña
              </label>
              <div className="mt-1">
                <input
                  id="new-password"
                  name="new-password"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="form-input"
                  placeholder="Mínimo 8 caracteres"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Debe incluir: mayúsculas, minúsculas y números
              </p>
            </div>

            <div>
              <label htmlFor="confirm-password" className="form-label">
                Confirmar contraseña
              </label>
              <div className="mt-1">
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="form-input"
                  placeholder="Repite la nueva contraseña"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !newPassword || !confirmPassword}
                className="btn-primary w-full disabled:opacity-50"
              >
                {loading ? 'Guardando nueva contraseña...' : 'Guardar nueva contraseña'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Public Reset Success Page Component

export default PublicResetPasswordPage;
